import type { ReactNode } from 'react';
import { LEGAL } from '@/config/legal';
import legalDocuments from '@/content/legalDocuments.json';

type LegalBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'subsection'; number: string; text: string };

type LegalSection = {
  title: string;
  blocks: LegalBlock[];
};

function linkifyContactEmail(text: string): ReactNode {
  const email = LEGAL.contactEmail;
  if (!text.includes(email)) {
    return text;
  }
  const parts = text.split(email);
  const nodes: ReactNode[] = [];
  parts.forEach((part, index) => {
    if (part) nodes.push(part);
    if (index < parts.length - 1) {
      nodes.push(
        <a
          key={`mailto-${index}`}
          href={`mailto:${email}`}
          className="text-brand-deep hover:text-brand hover:underline"
        >
          {email}
        </a>
      );
    }
  });
  return nodes;
}

function LegalBlocks({ blocks }: { blocks: LegalBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === 'subsection') {
          return (
            <div key={`${block.number}-${index}`} className="space-y-1">
              <p className="font-medium text-foreground">{block.number}</p>
              <p>{linkifyContactEmail(block.text)}</p>
            </div>
          );
        }
        const trimmed = block.text.trim();
        if (trimmed === LEGAL.contactEmail) {
          return (
            <p key={index}>
              <a
                href={`mailto:${LEGAL.contactEmail}`}
                className="text-brand-deep hover:text-brand hover:underline"
              >
                {LEGAL.contactEmail}
              </a>
            </p>
          );
        }
        return <p key={index}>{linkifyContactEmail(block.text)}</p>;
      })}
    </>
  );
}

function DocumentSections({ sections }: { sections: LegalSection[] }) {
  return (
    <div className="space-y-5 text-body-sm text-foreground leading-relaxed">
      {sections.map((section) => (
        <section key={section.title} className="space-y-2 border-t border-border pt-4 first:border-t-0 first:pt-0">
          <h3 className="text-body-md font-semibold text-foreground">{section.title}</h3>
          <div className="space-y-2">
            <LegalBlocks blocks={section.blocks as LegalBlock[]} />
          </div>
        </section>
      ))}
    </div>
  );
}

export function TermsOfServiceBody() {
  return <DocumentSections sections={legalDocuments.terms.sections as LegalSection[]} />;
}

export function PrivacyPolicyBody() {
  return <DocumentSections sections={legalDocuments.privacy.sections as LegalSection[]} />;
}
